import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Space, Typography, Input, Select, Card, message, Divider } from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  DragOutlined, 
  SaveOutlined, 
  PlayCircleOutlined, 
  DatabaseOutlined, 
  SettingOutlined, 
  MergeOutlined, 
  ExportOutlined 
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { getNodeExecutionOrder, generateStepsFromNodes } from '../../utils/workflowUtils';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Node type to icon mapping
const NODE_TYPE_ICONS = {
  start: <PlayCircleOutlined style={{ color: '#1890ff' }} />,
  dataSource: <DatabaseOutlined style={{ color: '#52c41a' }} />,
  transformer: <SettingOutlined style={{ color: '#1890ff' }} />,
  join: <MergeOutlined style={{ color: '#fa8c16' }} />,
  output: <ExportOutlined style={{ color: '#722ed1' }} />,
};

// Node type display names
const NODE_TYPE_NAMES = {
  start: 'Start',
  dataSource: 'Data Source',
  transformer: 'Transformer',
  join: 'Join',
  output: 'Output',
};

// Draggable step item component
const DraggableStepItem = ({ step, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStepIcon = () => {
    return NODE_TYPE_ICONS[step.nodeType] || <SettingOutlined />;
  };

  const getStepTitle = () => {
    // Priority: customName > config.node_name > nodeLabel > default
    // console.log('step.config: ', step.config)
    // Check if there's a node_name in the step's config
    if (step.config?.node_name) {
      return step.config.node_name;
    }
    
    // Use nodeLabel if available
    if (step.nodeLabel) {
      return step.nodeLabel;
    }
    
    // Fallback to node type
    return `${NODE_TYPE_NAMES[step.nodeType] || 'Unknown'} - Unnamed`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="workflow-step-item"
    >
      <Card 
        size="small" 
        style={{ 
          marginBottom: 8,
          border: '1px solid #d9d9d9',
          cursor: 'move'
        }}
        bodyStyle={{ 
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        <div {...listeners} style={{ cursor: 'grab', color: '#8c8c8c' }}>
          <DragOutlined />
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getStepIcon()}
          <Text strong>{index + 1}</Text>
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500 }}>{getStepTitle()}</div>
          <div style={{ 
            fontSize: '12px', 
            color: '#8c8c8c',
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {step.description || 'No description provided'}
          </div>
        </div>
        
        <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
          {NODE_TYPE_NAMES[step.nodeType]}
        </div>
      </Card>
    </div>
  );
};

const WorkflowStepsEditor = ({ 
  open, 
  onClose, 
  onSave, 
  workflow,
  nodes,
  edges 
}) => {
  const [steps, setSteps] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [hasModifiedOrder, setHasModifiedOrder] = useState(false);
  const [autoGeneratedSteps, setAutoGeneratedSteps] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Merge existing execution steps with current workflow nodes
  const mergeStepsWithNodes = (existingSteps, workflowNodes, workflowEdges) => {
    if (!workflowNodes || workflowNodes.length === 0) return existingSteps;

    // Get current node IDs from workflow
    const currentNodeIds = new Set(workflowNodes.map(node => node.id));
    
    // Get node IDs that are already in execution steps
    const existingNodeIds = new Set(existingSteps.map(step => step.nodeId));
    
    // Find missing nodes that need to be added
    const missingNodeIds = [...currentNodeIds].filter(nodeId => !existingNodeIds.has(nodeId));
    // console.log('missingNodeIds: ', missingNodeIds, existingNodeIds)
    
    // Generate the correct execution order using DFS
    const startNode = workflowNodes.find(node => node.type === 'start');
    if (!startNode) return existingSteps;

    const correctOrder = getNodeExecutionOrder(workflowNodes, workflowEdges, startNode.id);
    // console.log('correctOrder: ', correctOrder, 'workflowNodes length:', workflowNodes.length)
    // Create a new merged steps array
    const mergedSteps = [];
    
    correctOrder.forEach((nodeId, index) => {
      // Check if this node already has an execution step
      const existingStep = existingSteps.find(step => step.nodeId === nodeId);
      
      if (existingStep) {
        // Use existing step but update the order
        mergedSteps.push({
          ...existingStep,
          order: index
        });
      } else {
        // Create new step for missing node
        const node = workflowNodes.find(n => n.id === nodeId);
        if (node) {
          const nodeLabel = node.data?.config?.node_name || node.data?.config?.connectionName || node.data?.label || 'Unnamed';
          
          let description = '';
          if (node.type === 'transformer') {
            description = node.data?.config?.transformationRequest || '';
          } else if (node.type === 'join') {
            description = node.data?.config?.joinDescription || '';
          } else {
            description = node.data?.config?.description || '';
          }
          mergedSteps.push({
            id: `step-${node.id}`,
            nodeId: node.id,
            node_name: node.node_name,
            nodeType: node.type,
            nodeLabel: nodeLabel,
            customName: '',
            description: description,
            order: index,
            config: node.data?.config || {}
          });
        }
      }
    });
    // console.log('mergedSteps: ', mergedSteps)

    // Ensure start node is first and output node is last
    const startIndex = mergedSteps.findIndex(step => step.nodeType === 'start');
    const outputIndex = mergedSteps.findIndex(step => step.nodeType === 'output');

    if (startIndex > 0) {
      const startStep = mergedSteps.splice(startIndex, 1)[0];
      mergedSteps.unshift(startStep);
    }

    if (outputIndex !== -1 && outputIndex < mergedSteps.length - 1) {
      const outputStep = mergedSteps.splice(outputIndex, 1)[0];
      mergedSteps.push(outputStep);
    }

    // Update order property after reordering
    return mergedSteps.map((step, index) => ({
      ...step,
      order: index
    }));
  };

  // Check if all nodes are covered by existing execution steps
  const areAllNodesInExecutionSteps = (existingSteps, workflowNodes) => {
    if (!existingSteps || !workflowNodes) return false;
    
    // Get node IDs from workflow
    const currentNodeIds = new Set(workflowNodes.map(node => node.id));
    
    // Get node IDs from execution steps
    const executionStepNodeIds = new Set(existingSteps.map(step => step.nodeId));
    
    // Check if all current nodes are in execution steps
    for (const nodeId of currentNodeIds) {
      if (!executionStepNodeIds.has(nodeId)) {
        console.log('Missing node in execution steps:', nodeId);
        return false;
      }
    }
    
    console.log('All nodes are covered by existing execution steps');
    return true;
  };

  // Initialize steps from workflow or derive from nodes
  useEffect(() => {
    if (open) {
      if (workflow?.executionSteps && workflow.executionSteps.length > 0) {
        const existingSteps = [...workflow.executionSteps];
        
        // Check if all current nodes are already in execution steps
        if (areAllNodesInExecutionSteps(existingSteps, nodes)) {
          // All nodes are covered, keep existing execution steps as-is
          console.log('Using existing execution steps - all nodes are covered, keeping current order');
          setSteps(existingSteps);
          setHasModifiedOrder(true);
          setAutoGeneratedSteps([]);
        } else {
          // New node detected - do a full rebuild of steps
          console.log('New node detected - doing full rebuild of execution steps');
          const generatedSteps = generateStepsFromNodes(nodes, edges);
          setSteps(generatedSteps);
          setAutoGeneratedSteps([...generatedSteps]);
          setHasModifiedOrder(false);
        }
      } else if (nodes && nodes.length > 0) {
        // Auto-generate steps from workflow nodes
        console.log('Auto-generating execution steps from nodes');
        const generatedSteps = generateStepsFromNodes(nodes, edges);
        setSteps(generatedSteps);
        setAutoGeneratedSteps([...generatedSteps]); // Keep track of original auto-generated order
        setHasModifiedOrder(false);
      } else {
        setSteps([]);
        setAutoGeneratedSteps([]);
        setHasModifiedOrder(false);
      }
    }
  }, [open, workflow, nodes, edges]);


  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setSteps((prevSteps) => {
        const draggedStep = prevSteps.find(step => step.id === active.id);
        const targetStep = prevSteps.find(step => step.id === over.id);

        // Prevent moving start or output nodes
        if (draggedStep?.nodeType === 'start' || draggedStep?.nodeType === 'output') {
          message.warning('Start and Output nodes cannot be reordered. Start must be first, Output must be last.');
          return prevSteps;
        }

        const oldIndex = prevSteps.findIndex(step => step.id === active.id);
        const newIndex = prevSteps.findIndex(step => step.id === over.id);

        // Prevent dropping on start or output positions
        if (newIndex === 0 || (targetStep?.nodeType === 'output')) {
          message.warning('Cannot place nodes before Start or after Output.');
          return prevSteps;
        }

        const reorderedSteps = arrayMove(prevSteps, oldIndex, newIndex);
        
        // Ensure start is still first and output is still last
        const startIndex = reorderedSteps.findIndex(step => step.nodeType === 'start');
        const outputIndex = reorderedSteps.findIndex(step => step.nodeType === 'output');

        if (startIndex !== 0 && startIndex !== -1) {
          const startStep = reorderedSteps.splice(startIndex, 1)[0];
          reorderedSteps.unshift(startStep);
        }

        if (outputIndex !== -1 && outputIndex !== reorderedSteps.length - 1) {
          const outputStep = reorderedSteps.splice(outputIndex, 1)[0];
          reorderedSteps.push(outputStep);
        }

        // Update order property
        const finalSteps = reorderedSteps.map((step, index) => ({
          ...step,
          order: index
        }));

        // Mark as modified since user manually reordered
        setHasModifiedOrder(true);
        
        return finalSteps;
      });
    }
  };


  const handleSave = () => {
    // Prepare execution steps for saving
    const executionSteps = steps.map(step => ({
      id: step.id,
      nodeId: step.nodeId,
      nodeType: step.nodeType,
      nodeLabel: step.nodeLabel,
      customName: step.customName,
      description: step.description,
      order: step.order,
      config: step.config
    }));

    // Pass execution steps to parent component
    onSave(executionSteps);
    message.success('Execution steps saved to workflow');
    onClose();
  };

  const draggedStep = activeId ? steps.find(step => step.id === activeId) : null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SettingOutlined />
          <span>Edit Execution Steps</span>
          {workflow?.name && (
            <Text type="secondary">- {workflow.name}</Text>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      width={800}
      footer={
        <Space>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="primary" 
            icon={<SaveOutlined />}
            onClick={handleSave}
          >
            Save Steps
          </Button>
        </Space>
      }
    >
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <div style={{ marginBottom: 16 }}>
          <Text>
            Define the execution order of your workflow steps. Drag and drop to reorder, 
            or customize names and descriptions for each step.
          </Text>
        </div>

        {steps.length > 0 ? (
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={steps.map(step => step.id)}
              strategy={verticalListSortingStrategy}
            >
              {steps.map((step, index) => (
                <DraggableStepItem 
                  key={step.id} 
                  step={step} 
                  index={index}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {draggedStep && (
                <DraggableStepItem 
                  step={draggedStep} 
                  index={steps.findIndex(s => s.id === draggedStep.id)}
                />
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          <Card style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">
              No execution steps defined. Add steps to customize the execution order.
            </Text>
          </Card>
        )}

        <Divider />
        
      </div>
    </Modal>
  );
};

export default WorkflowStepsEditor;